import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { startGoogleAuth } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account creato!");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di registrazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-[#F8F8F6] order-2 md:order-1">
        <div className="w-full max-w-sm">
          <div className="overline text-[#0A0A0A] mb-3">— REGISTRATI</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Crea account.</h1>
          <p className="text-sm text-[#5C5C5C] mb-10">Gratis. Nessuna carta richiesta.</p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs uppercase tracking-widest">Nome</Label>
              <Input data-testid="register-name-input" id="name" required value={name}
                onChange={e=>setName(e.target.value)} className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
              <Input data-testid="register-email-input" id="email" type="email" required value={email}
                onChange={e=>setEmail(e.target.value)} className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest">Password (min 8)</Label>
              <Input data-testid="register-password-input" id="password" type="password" required minLength={8} value={password}
                onChange={e=>setPassword(e.target.value)} className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]" />
            </div>
            <Button data-testid="register-submit-btn" disabled={loading} type="submit"
              className="w-full rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11">
              {loading ? "Creazione…" : (<>Crea account <ArrowRight className="ml-2 w-4 h-4"/></>)}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-black/10" />
            <span className="text-xs text-[#8A8A8A] uppercase tracking-widest">oppure</span>
            <div className="flex-1 h-px bg-black/10" />
          </div>

          <button
            data-testid="google-register-btn"
            type="button"
            onClick={startGoogleAuth}
            className="w-full flex items-center justify-center gap-3 border border-black/15 hover:bg-black/5 rounded-xl h-11 text-sm font-medium transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continua con Google
          </button>

          <div className="mt-8 text-sm text-[#5C5C5C]">
            Hai già un account? <Link data-testid="register-goto-login" to="/login" className="text-[#0A0A0A] hover:underline">Accedi</Link>
          </div>
        </div>
      </div>

      <div className="hidden md:block relative order-1 md:order-2">
        <img
          src="https://images.pexels.com/photos/4258830/pexels-photo-4258830.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="paper documents"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/50" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2 justify-end">
            <span className="font-display font-bold text-lg">Archivio Smart</span>
            <div className="w-8 h-8 bg-white flex items-center justify-center rounded-xl">
              <ScanLine className="w-4 h-4 text-[#0A0A0A]" />
            </div>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">— TEMPO RISPARMIATO</div>
            <p className="font-display text-3xl leading-tight max-w-sm ml-auto text-right">
              Ore alla settimana risparmiate.
              Ordine mentale, garantito.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
